import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  trackFirstProjectComplete,
  trackProjectComplete,
  trackClickRemoveWatermark,
} from '../../../analytics';

import { Button, ButtonLink, Alert } from '../../../components/general';
import { PageWrapper, BackBtn } from '../../../components/layout/content';
import { ConfirmModal, ErrorModal } from '../../../components/modals';

import config from '../../../config';

import {
  URL,
  FACE_EMAIL_STATUS,
  FIRST_EVENT_PROMO_CODE,
  UG_ID,
} from '../../../constants';

import useAuthUserStore from '../../../hooks/useAuthUserStore';
import useSampleProjectData from '../../../hooks/useSampleProjectData';

import { getCreationResults } from '../../../services/api/creations';
import { getTemplate } from '../../../services/api/emails';
import { getSelectionEmails } from '../../../services/emailTemplatesService';

import { useProjectStore } from '../../../stores/project';
import { useProjectSettingsStore } from '../../../stores/projectSettings';

import {
  Event,
  Person,
  PeopleEmailState,
  SGTemplate,
  PageError,
} from '../../../types';
import {
  goTo,
  isEmpty,
  getUrl,
  getShareLink,
  getCreationVideoUrl,
} from '../../../utils';

import EmailPreviewModal from '../../ProjectDashboard/Settings/EmailHubTab/components/emailPreviewModal';
import DoneBtn from '../components/doneBtn';
import MainShareBtn from './components/mainShareBtn';
import ShareFaceBlock from './components/shareFaceBlock';
import SharePageHeader from './components/sharePageHeader';
import StatsBlock from './components/statsBlock';

const { stripeLinks } = config;

const Share = () => {
  const { eventId = '' } = useParams();

  const queryParameters = new URLSearchParams(window.location.search);
  const payment_success = queryParameters.get('payment_success') === 'true';

  const { user } = useAuthUserStore();
  const {
    eventId: eventIdLocal,
    userEvents,
    currentProject,
    projectError,
    setEventId,
    setSampleProject,
  } = useProjectStore();
  const { footer, setFooter } = useProjectSettingsStore();

  const [people, setPeople] = useState<Person[]>([]);
  const [emailState, setEmailState] = useState<PeopleEmailState>({});
  const [creationTemplate, setCreationTemplate] = useState<SGTemplate>({});

  const [isIncreased, setIsIncreased] = useState(false);
  const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState<PageError>(null);
  const [error, setError] = useState('');

  // sample project data
  const { isSample, sampleProjectData } = useSampleProjectData(eventId);

  const { creationTemplateId, creationTemplateTitle, creationTemplateText } =
    useMemo(() => {
      const creationTemplateId = currentProject?.emailsData?.creationTemplateId;
      const creationTemplateText =
        currentProject?.emailsData?.creationTemplateText;

      const creationTemplateTitle =
        getSelectionEmails('creation').find(
          (t) => t.templateId === creationTemplateId,
        )?.title ?? '';

      return {
        creationTemplateId,
        creationTemplateTitle,
        creationTemplateText,
      };
    }, [currentProject?.emailsData]);

  // set event id on mount
  useEffect(() => {
    if (isSample) setSampleProject(eventId);
    else if (eventIdLocal !== eventId) setEventId(eventId);
  }, [eventId, eventIdLocal, isSample, setSampleProject, setEventId]);

  // fetch data on mount
  useEffect(() => {
    const onMount = async () => {
      setIsLoading(true);

      await fetchGenerationData();
      await fetchEmailTemplateData();

      if (payment_success) {
        setShowPaymentModal(true);
        window.history.pushState({}, '', getUrl(URL.VB_SHARE, eventId));
      }

      handleAnalytics();

      setIsLoading(false);
    };

    if (projectError) {
      setPageError(projectError);
      setIsLoading(false);
      return;
    }

    if (currentProject) onMount();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject, projectError]);

  useEffect(() => {
    const peopleEmailStateTemp: PeopleEmailState = {};

    people.forEach(
      (person) =>
        (peopleEmailStateTemp[person.face.elementId] = {
          email: person.face.email || '',
          status: !!person.metrics?.emailedCount
            ? FACE_EMAIL_STATUS.RESEND
            : FACE_EMAIL_STATUS.NOT_SENT,
          err: null,
        }),
    );

    setEmailState(peopleEmailStateTemp);
  }, [people]);

  // trigger sample User Guiding if required
  useEffect(() => {
    const isShow = sessionStorage.getItem('showSampleUG') === 'true';
    if (isShow) {
      window.userGuiding.previewGuide(UG_ID.SAMPLE_P3);
      sessionStorage.removeItem('showSampleUG');
    }
  }, []);

  const handleAnalytics = () => {
    if (!currentProject) return;

    // add project completion analytics
    // check if eventId is not in localStorage completedProjects – to avoid duplicate events
    const completedProjects = JSON.parse(
      localStorage.getItem('completedProjects') ?? '[]',
    );

    if (
      Array.isArray(completedProjects) &&
      !completedProjects.includes(eventId)
    ) {
      trackProjectComplete(currentProject);

      localStorage.setItem(
        'completedProjects',
        JSON.stringify([...completedProjects, eventId]),
      );
    }

    // when user opens share page for the first time – add GA events
    const isFirstCompletedProject =
      userEvents.filter((event: Event) => event.stage === 'generation')
        ?.length === 1 &&
      localStorage.getItem('hasFirstCompletedProject') !== 'true';

    // if it's first completed project – add corresponding analytics
    if (isFirstCompletedProject) {
      trackFirstProjectComplete(currentProject);

      localStorage.setItem('hasFirstCompletedProject', 'true');
    }
  };

  const fetchGenerationData = async () => {
    if (!currentProject) return;

    try {
      let generatedPeople: Person[] = [];

      // sample project
      if (isSample) {
        sampleProjectData.people.forEach((person: any) => {
          if (person.isSelected && person.isGenerated) {
            const face = person.face;

            const foundCreation = sampleProjectData.creations[
              face.elementId
            ].find((creation: any) => creation.isSelected);

            // imitate data returned by getCreationResults
            generatedPeople.push({
              face,
              creations: [foundCreation],
              img: person.files[0].url,
              finalVideoUri: foundCreation.uri,
            });
          }
        });
      }

      // usual event
      else generatedPeople = await getCreationResults(eventId);

      // sort people and add links
      const peopleUpd = [...generatedPeople]
        .map((person) => ({
          ...person,
          shareLink: getShareLink(eventId, person.face.elementId),
          videoUrl: getCreationVideoUrl(currentProject, person),
        }))
        .sort((one, other) => {
          if (one.face.email && !other.face.email) return -1;
          if (!one.face.email && other.face.email) return 1;

          return (
            new Date(one.face.created).getTime() -
            new Date(other.face.created).getTime()
          );
        });

      setPeople(peopleUpd);
    } catch (err) {
      console.error(`Error fetching the generated people: ${err}`);
      setError('Error fetching the generated people');
    }
  };

  const fetchEmailTemplateData = async () => {
    if (creationTemplateId) {
      const template = await getTemplate(creationTemplateId);
      setCreationTemplate(template);

      if (isEmpty(footer)) await setFooter();
    }
  };

  const afterEmailSend = () => {
    fetchGenerationData();
  };

  return (
    <PageWrapper
      type="project"
      title={`Share – ${currentProject?.name}`}
      isLoading={isLoading}
      pageError={pageError}
    >
      <BackBtn label="Video Builder" />

      <div className="flex flex-col gap-6">
        <SharePageHeader
          people={people}
          isIncreased={isIncreased}
          setIsIncreased={setIsIncreased}
        />

        {isEmpty(people) ? (
          <React.Fragment>
            <Alert
              alertData={{
                variant: 'warning',
                text: (
                  <>
                    Hmm, seems like you don't have any videos generated yet.
                    Come back when you complete all{' '}
                    <Link to={getUrl(URL.VIDEO_BUILDER, eventId)}>
                      Video Builder
                    </Link>{' '}
                    steps.
                  </>
                ),
              }}
            />

            <DoneBtn isBack />
          </React.Fragment>
        ) : (
          <React.Fragment>
            {!currentProject?.payment?.paid && (
              <div className="animated-box-primary flex flex-col sm:flex-row items-center p-[2px] rounded-md animate-running-2">
                <div className="w-full flex flex-row items-center justify-between p-3 sm:p-4 gap-2 sm:gap-4 text-left text-black bg-white rounded-t sm:rounded-l sm:rounded-tr-none">
                  <div>
                    Remove watermarks now for only{' '}
                    <span className="line-through">$249</span>{' '}
                    <span className="font-bold text-[1.1rem] bg-gradient-primary-right bg-clip-text text-transparent">
                      $39
                    </span>
                  </div>
                </div>

                <ButtonLink
                  to={`${stripeLinks.removeWatermarkProject}?client_reference_id=${eventId}&prefilled_email=${user.email}&prefilled_promo_code=${FIRST_EVENT_PROMO_CODE}`}
                  onClick={trackClickRemoveWatermark}
                  variant="custom"
                  colour="custom"
                  className="w-full sm:w-32 p-3 sm:p-4 text-center text-white whitespace-nowrap duration-200 sm:hover:w-72"
                >
                  Pay now
                </ButtonLink>
              </div>
            )}

            {!isSample && <StatsBlock people={people} />}

            <div className="w-full flex flex-col items-center gap-2">
              <MainShareBtn
                people={people}
                emailState={emailState}
                setEmailState={setEmailState}
                creationTemplate={creationTemplate}
                afterEmailSend={afterEmailSend}
              />

              {!isEmpty(creationTemplate) && (
                <div className="text-sm text-black/[0.5]">
                  <>
                    The attendees will receive{' '}
                    <Button
                      onClick={() => setShowEmailPreviewModal(true)}
                      variant="link"
                    >
                      {creationTemplateTitle}
                    </Button>{' '}
                    email. Change template{' '}
                    <Link
                      to={`${getUrl(URL.PROJECT_SETTINGS, eventId, 'email-hub/creation')}?redirectUrl=${getUrl(URL.VB_SHARE, eventId)}`}
                    >
                      here
                    </Link>
                  </>
                </div>
              )}
            </div>

            <div className="w-full flex flex-wrap gap-8">
              {people.map((person) => (
                <ShareFaceBlock
                  key={person.face.elementId}
                  person={person}
                  emailState={emailState}
                  setEmailState={setEmailState}
                  afterEmailSend={afterEmailSend}
                  isIncreased={isIncreased}
                />
              ))}
            </div>
          </React.Fragment>
        )}
      </div>

      {/* email preview modal */}
      <EmailPreviewModal
        show={showEmailPreviewModal}
        onHide={() => setShowEmailPreviewModal(false)}
        template={creationTemplate}
        customText={creationTemplateText}
      />

      {/* payment modal */}
      <ConfirmModal
        show={showPaymentModal}
        onHide={() => setShowPaymentModal(false)}
        heading="Payment successful"
        subheading="Thanks for upgrading your project. We've now generated watermark-free highlight reels for everyone to share and enjoy."
        btns={[
          { label: 'See Videos', onClick: () => setShowPaymentModal(false) },
        ]}
      />

      <ErrorModal
        show={!!error}
        heading="Something went wrong"
        subheading={error}
        isTryAgainBtn
      />
    </PageWrapper>
  );
};

export default Share;
